import { ReactNode, ReactElement, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/system/colorManipulator';
import styled, { css } from 'styled-components';

// Components
import { IconChevronDown, IconChevronRight } from '@u21/tabler-icons';
import { Collapse, ListItemButton, ListItemText } from '@mui/material';

// Theme
import { selectFeatureFlags } from 'app/shared/featureFlags/selectors';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useSelector } from 'react-redux';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';
import { U21NewFeatureLabel } from 'app/shared/u21-ui/components/display/U21NewFeatureLabel';
import { U21Chip } from 'app/shared/u21-ui/components/display/U21Chip';
import { convertStyleObjectToCSSString } from 'app/shared/utils/react';

const ListItemStyle = styled(ListItemButton)<{
  $groupFirst?: boolean;
  $nested?: boolean;
}>`
  ${({ theme }) => convertStyleObjectToCSSString(theme.typography.body2)}
  height: 48px;
  width: 180px;
  text-transform: capitalize;
  padding-left: ${({ $nested }) => ($nested ? '45px' : '18px')};
  margin-top: ${({ $groupFirst }) => ($groupFirst ? '48px' : undefined)};
  color: ${({ theme }) => theme.palette.text.secondary};
  stroke: ${({ theme }) => theme.palette.text.secondary};
  stroke-width: 2;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  &.isActiveRoot {
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    color: ${({ theme }) => theme.palette.primary.main};
    stroke: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) =>
      alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)};

    &:before {
      top: 0;
      right: 0;
      width: 3px;
      bottom: 0;
      content: '';
      position: absolute;
      background-color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const ListItemTextStyle = styled(ListItemText)<{ $newLabel: boolean }>`
  ${({ $newLabel }) =>
    $newLabel
      ? css`
          padding: 0 10px;
          flex: 0 0 auto;
        `
      : css`
          padding-left: 10px;
          padding-right: 10px;
        `}
`;

interface NavItemProps {
  level?: number;
  title?: string;
  href?: string;
  icon?: ReactElement;
  children?: ReactNode;
  disabled?: boolean;
  onClick?: any;
  groupFirst?: boolean;
  newLabel?: boolean | string;
  isCollapsed?: boolean;
}

export default function SidebarItem({
  level = 0,
  title,
  href,
  icon,
  onClick,
  children,
  disabled = false,
  groupFirst = false,
  newLabel = false,
  isCollapsed = false,
}: NavItemProps) {
  const [show, setShow] = useState(true);
  const featureFlags = useSelector(selectFeatureFlags);

  const showNewLabel =
    (typeof newLabel === 'boolean' && newLabel) ||
    (typeof newLabel === 'string' && featureFlags[newLabel]) ||
    false;

  if (children) {
    return (
      <>
        <ListItemStyle
          disabled={disabled}
          disableGutters
          onClick={() => setShow((currShow) => !currShow)}
        >
          {show && <IconChevronDown />}
          {!show && <IconChevronRight />}

          <ListItemTextStyle
            disableTypography
            primary={title}
            $newLabel={showNewLabel}
          />
        </ListItemStyle>

        <Collapse in={show}>{children}</Collapse>
      </>
    );
  }

  const renderIcon = () => {
    if (!icon) return null;
    if (isCollapsed && title) {
      return (
        <U21Tooltip tooltip={title} placement="right" enterDelay={0}>
          {icon}
        </U21Tooltip>
      );
    }
    return icon;
  };

  return (
    <ListItemStyle
      $nested={level > 0}
      $groupFirst={groupFirst}
      // @ts-expect-error TS type limitation
      to={href || {}}
      exact={false}
      disableGutters
      component={RouterLink}
      activeClassName="isActiveRoot"
      onClick={onClick}
    >
      {renderIcon()}
      {title && !isCollapsed && (
        <ListItemTextStyle
          disableTypography
          primary={title}
          $newLabel={showNewLabel}
        />
      )}
      {title && showNewLabel && !isCollapsed && (
        <>
          {title === 'AI' ? (
            <U21Chip color="special" rounded variant="ghost">
              New
            </U21Chip>
          ) : (
            <U21NewFeatureLabel />
          )}
        </>
      )}
    </ListItemStyle>
  );
}
